import React, { ReactNode, useState, useRef, useEffect } from 'react';
import './AIEditor.scss';
import LeftPanelHeader from './components/LeftPanelHeader';
import LeftPanelVisualizer from './components/LeftPanelVisualizer';
import RightPanelHeader from './components/RightPanelHeader';
import RightPanelEditor from './components/RightPanelEditor';
import RightPanelFooter from './components/RightPanelFooter';
import AIAnalysisModule from '../RightPanelModules/AIAnalysis/AIAnalysisModule';
import SavedResponsesList from './components/SavedResponsesList';
import { AIEditorCacheService } from './services/AIEditorCacheService';
import { PromptVersionService, PromptVersion, PromptVersionGroup } from './services/PromptVersionService';
import { ReportViewModel } from 'types/report';
import { useAIExtendedContext } from 'hooks/useAIExtendedContext';

export interface TabItem {
  id: string;
  label: string;
  callback?: () => void;
}

export interface VariableData {
  [key: string]: string | object;
}

export interface SavedResponse {
  id: string;
  title: string;
  content: string;
  timestamp: Date;
  source?: string; // e.g., 'ChatGPT', 'Claude', 'Manual', 'Run'
  model?: string; // e.g., 'gpt-4o', 'gpt-4o-mini', etc.
  executionTime?: number; // execution time in milliseconds
}

export interface AIEditorProps {
  prompt?: string;
  variableData?: VariableData;
  previewContent?: string | ReactNode;
  onClose?: () => void;
  onRun?: (prompt: string, variables: VariableData, model?: string) => Promise<string>;
  onDownload?: () => void;
  additionalTabs?: TabItem[];
  activeTabId?: string;
  onTabChange?: (tabId: string) => void;
  reportData?: ReportViewModel | null; // Add report data for cache integration
  onConstrainChange?: (isConstrained: boolean) => void; // Add constrain callback
}

const AIEditor: React.FC<AIEditorProps> = ({
  prompt = '',
  variableData = {},
  previewContent = '',
  onClose,
  onRun,
  onDownload,
  additionalTabs = [],
  activeTabId = 'preview',
  onTabChange,
  reportData,
  onConstrainChange,
}) => {
  // Get extended context for AI prompts
  const extendedContext = useAIExtendedContext();
  const [editorContent, setEditorContent] = useState(prompt);
  const [initialContentLoaded, setInitialContentLoaded] = useState(false);
  // Version management state
  const [versionGroup, setVersionGroup] = useState<PromptVersionGroup | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // percentage
  const [currentTabId, setCurrentTabId] = useState(activeTabId);
  const [currentLeftPanelContent, setCurrentLeftPanelContent] = useState<string | ReactNode>(previewContent);
  const [currentLeftPanelType, setCurrentLeftPanelType] = useState<'markdown' | 'json' | 'component'>('markdown');
  const [isLoading, setIsLoading] = useState(false);
  const [resolvedPrompt, setResolvedPrompt] = useState<string>('');
  const [llmResponse, setLlmResponse] = useState<string>('');
  const [pastedResponse, setPastedResponse] = useState<string>('');
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [isDataMode, setIsDataMode] = useState(false);
  const [selectedResponseId, setSelectedResponseId] = useState<string | null>(null);
  const [selectedVariableId, setSelectedVariableId] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>('gpt-4o-mini');
  const [savedResponses, setSavedResponses] = useState<SavedResponse[]>([]);
  const [selectedSavedResponse, setSelectedSavedResponse] = useState<SavedResponse | null>(null);
  const [displayPreviewContent, setDisplayPreviewContent] = useState<string | ReactNode>(previewContent);
  const [isVarEditMode, setIsVarEditMode] = useState(false);
  const [editableVariableData, setEditableVariableData] = useState<VariableData>(variableData);
  const [isConstrained, setIsConstrained] = useState(false);

  // Sync editable variable data with original variable data when it changes
  useEffect(() => {
    setEditableVariableData({
      ...variableData,
      extendedContext, // Always add extendedContext, even if empty initially
    });
  }, [variableData, extendedContext]);

  // Extract variables from the prompt
  const extractVariables = (promptText: string): string[] => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(promptText)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }

    return variables;
  };

  // Generate tabs from variables
  const generateTabs = (): TabItem[] => {
    const variables = extractVariables(editorContent);
    const variableTabs: TabItem[] = variables.map(variable => ({
      id: variable,
      label: variable,
      callback: () => handleVariableTabClick(variable),
    }));

    // Add system tabs first
    const allTabs: TabItem[] = [
      { id: 'preview', label: 'Preview', callback: () => handlePreviewTabClick() },
      { id: 'resolved-prompt', label: 'Resolved Prompt', callback: () => handleResolvedPromptTabClick() },
      { id: 'vars', label: 'Vars', callback: () => handleVarsTabClick() },
      ...additionalTabs,
    ];

    return allTabs;
  };

  // Handle variable data changes in edit mode
  const handleVariableDataChange = (newData: string) => {
    // Use currentTabId as the variable ID when we're in a variable tab
    const variableId = Object.keys(editableVariableData).includes(currentTabId) ? currentTabId : selectedVariableId;

    if (variableId) {
      // Don't try to parse as JSON automatically - keep it as string for editing
      setEditableVariableData(prev => ({
        ...prev,
        [variableId]: newData,
      }));
    }
  };

  // Resolve variables in the prompt
  const resolvePromptVariables = (promptText: string, variables: VariableData): string => {
    let resolvedPrompt = promptText;

    Object.entries(variables).forEach(([key, value]) => {
      const variablePattern = new RegExp(`{{${key}}}`, 'g');
      const replacementValue = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
      resolvedPrompt = resolvedPrompt.replace(variablePattern, replacementValue);
    });

    return resolvedPrompt;
  };

  // Handle any tab change - clear saved response selection and handle tab switching
  const handleTabChange = (tabId: string) => {
    // ALWAYS clear saved response selection when switching tabs - this must happen first
    if (selectedSavedResponse) {
      setSelectedSavedResponse(null);
      setSelectedResponseId(null);
    }

    // Handle different tab types
    const variables = extractVariables(editorContent);

    if (variables.includes(tabId)) {
      // This is a variable tab
      if (currentTabId === 'vars') {
        // If we're in vars tab, just update the selected variable
        setSelectedVariableId(tabId);
      } else {
        // Otherwise, switch to the variable tab directly
        setCurrentTabId(tabId);
      }
    } else if (additionalTabs.some(tab => tab.id === tabId)) {
      // This is an additional tab - switch to it and call parent onTabChange
      setCurrentTabId(tabId);
      if (onTabChange) {
        onTabChange(tabId);
      }
    } else if (!['preview', 'resolved-prompt', 'vars', 'saved-responses'].includes(tabId)) {
      // This might be another type of tab - still switch to it
      setCurrentTabId(tabId);
      if (onTabChange) {
        onTabChange(tabId);
      }
    }
    // Built-in tabs (preview, resolved-prompt, vars, saved-responses) are handled by their callbacks
  };

  // Handle variable tab click - set selected variable when in vars context, otherwise switch tab
  const handleVariableTabClick = (variable: string) => {
    handleTabChange(variable);
  };

  // Handle preview tab click - just set the tab ID, content is managed by useEffect
  const handlePreviewTabClick = () => {
    // Clear saved response selection when switching tabs
    if (selectedSavedResponse) {
      setSelectedSavedResponse(null);
      setSelectedResponseId(null);
    }

    setCurrentTabId('preview');
    // When switching to preview tab, if we have an LLM response, set the content type to component
    if (llmResponse || pastedResponse) {
      setCurrentLeftPanelType('component');
    }
  };

  // Handle resolved prompt tab click - just set the tab ID, content is managed by useEffect
  const handleResolvedPromptTabClick = () => {
    // Clear saved response selection when switching to resolved prompt
    if (selectedSavedResponse) {
      setSelectedSavedResponse(null);
      setSelectedResponseId(null);
    }

    setResolvedPrompt(resolvePromptVariables(editorContent, editableVariableData));
    setCurrentTabId('resolved-prompt');
  };

  // Handle saved responses tab click
  const handleSavedResponsesTabClick = () => {
    // Clear saved response selection when switching to saved responses
    setCurrentTabId('saved-responses');
  };

  // Handle vars tab click
  const handleVarsTabClick = () => {
    // Clear saved response selection when switching tabs
    if (selectedSavedResponse) {
      setSelectedSavedResponse(null);
      setSelectedResponseId(null);
    }

    setCurrentTabId('vars');
  };

  // Update tabs when editor content changes
  useEffect(() => {
    // If the current tab is a variable that no longer exists, switch to preview

    const variables = extractVariables(editorContent);

    // Only switch tabs if the current tab is a variable that no longer exists
    // AND it's not one of the fixed tabs (preview, resolved-prompt, vars, saved-responses)
    if (
      currentTabId !== 'preview' &&
      currentTabId !== 'resolved-prompt' &&
      currentTabId !== 'vars' &&
      currentTabId !== 'saved-responses' &&
      !variables.includes(currentTabId) &&
      !additionalTabs.some(tab => tab.id === currentTabId)
    ) {
      setCurrentTabId('preview');
    }
  }, [editorContent, additionalTabs, currentTabId]);

  // Load version data and cached responses on mount
  useEffect(() => {
    if (reportData) {
      // Load version data (don't auto-initialize to avoid creating cache entries on every page load)
      const group = PromptVersionService.getVersionGroup(reportData);
      setVersionGroup(group);

      // If we have a version group, set the editor content to the active version
      if (group) {
        const activeVersion = group.versions.find(v => v.id === group.activeVersionId);
        if (activeVersion && activeVersion.content !== editorContent) {
          setEditorContent(activeVersion.content);
        }
      }

      // Load any cached responses for this report
      const cachedGroup = AIEditorCacheService.getResponseGroup(reportData);

      const cachedResponses: SavedResponse[] = [];

      if (cachedGroup && cachedGroup.entries.length > 0) {
        cachedGroup.entries.forEach(entry => {
          cachedResponses.push({
            id: `cached-${entry.responseType}-${entry.timestamp}`,
            title:
              entry.title ||
              `${entry.responseType === 'external' ? 'External' : 'Internal'} - ${new Date(entry.timestamp).toLocaleDateString()}`,
            content: entry.response,
            timestamp: new Date(entry.timestamp),
            source: 'Cache',
            model: entry.model,
            executionTime: entry.executionTime,
          });
        });
      }

      if (cachedResponses.length > 0) {
        setSavedResponses(cachedResponses);
      }
    } else {
      // If no reportData, create a simple version group for basic functionality
      if (prompt) {
        const simpleGroup: PromptVersionGroup = {
          versions: [
            {
              id: 'original',
              name: 'Original',
              content: prompt,
              timestamp: Date.now(),
              isOriginal: true,
            },
          ],
          activeVersionId: 'original',
          lastModified: Date.now(),
        };
        setVersionGroup(simpleGroup);
      }
    }
  }, [reportData, prompt]);

  // Check for unsaved changes whenever editor content changes
  useEffect(() => {
    if (versionGroup && reportData) {
      const hasChanges = PromptVersionService.hasUnsavedChanges(reportData, editorContent);
      setHasUnsavedChanges(hasChanges);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [editorContent, versionGroup, reportData]);

  // Initialize with cached content or existing AIAnalysis data on first load
  useEffect(() => {
    if (!initialContentLoaded) {
      // Try to populate with existing AIAnalysis cache first (only if no prompt and no version group)
      if (!prompt && !versionGroup) {
        const initialContent = AIEditorCacheService.getInitialContentFromAIAnalysis(reportData);
        if (initialContent) {
          setEditorContent(initialContent);
        }
      }

      setInitialContentLoaded(true);
    }

    // Initialize with the correct content based on active tab
    if (activeTabId === 'preview') {
      handlePreviewTabClick();
    } else if (Object.keys(variableData).includes(activeTabId)) {
      handleVariableTabClick(activeTabId);
    }
  }, [initialContentLoaded, reportData, prompt, activeTabId, variableData, versionGroup]);

  // Keep preview content static (do not override with LLM response)
  // If needed in the future, we can add a setting to mirror LLM response into preview.

  // Handle content display based on current tab
  useEffect(() => {
    if (currentTabId === 'preview') {
      if (isLoading) {
        setCurrentLeftPanelType('markdown');
        setCurrentLeftPanelContent('Loading response...');
      } else if (selectedSavedResponse) {
        // Determine the current response content
        let currentResponse = '';
        if (selectedSavedResponse) {
          currentResponse = selectedSavedResponse.content;
        } else if (pastedResponse) {
          currentResponse = pastedResponse;
        } else if (llmResponse) {
          currentResponse = llmResponse;
        }

        if (currentResponse) {
          if (isDataMode) {
            // Show raw data/text
            setCurrentLeftPanelType('markdown');
            setCurrentLeftPanelContent(currentResponse);
          } else {
            // Show processed component view
            setCurrentLeftPanelType('component');
            setCurrentLeftPanelContent(<AIAnalysisModule LLMResult={currentResponse} />);
          }
        } else {
          // Fall back to provided preview content
          setCurrentLeftPanelType(typeof displayPreviewContent === 'string' ? 'markdown' : 'component');
          setCurrentLeftPanelContent(displayPreviewContent);
        }
      }
    } else if (currentTabId === 'resolved-prompt') {
      setCurrentLeftPanelType('markdown');
      setCurrentLeftPanelContent(
        resolvedPrompt || 'Run the prompt to see the resolved version with variables substituted.'
      );
    } else if (currentTabId === 'vars') {
      // Vars tab - show selected variable content or default message
      if (selectedVariableId && Object.keys(editableVariableData).includes(selectedVariableId)) {
        const data = editableVariableData[selectedVariableId];
        if (data !== undefined && data !== null) {
          const contentType = typeof data === 'object' ? 'json' : 'markdown';
          setCurrentLeftPanelType(contentType);
          setCurrentLeftPanelContent(contentType === 'json' ? JSON.stringify(data, null, 2) : (data as string));
        } else {
          setCurrentLeftPanelType('markdown');
          setCurrentLeftPanelContent(`No data available for variable {{${selectedVariableId}}}.`);
        }
      } else {
        setCurrentLeftPanelType('markdown');
        setCurrentLeftPanelContent('Select a variable from the tabs above to edit its value.');
      }
    } else if (currentTabId === 'saved-responses') {
      setCurrentLeftPanelType('component');
      setCurrentLeftPanelContent(
        <SavedResponsesList
          responses={savedResponses}
          onSelect={response => {
            setSelectedSavedResponse(response);
            setSelectedResponseId(response.id);
            setCurrentTabId('preview');
          }}
          onDelete={handleResponseDelete}
          onPurgeAll={handlePurgeAllResponses}
        />
      );
    } else if (Object.keys(editableVariableData).includes(currentTabId)) {
      // Skip content updates when in edit mode to prevent interference with textarea
      if (!isVarEditMode) {
        const data = editableVariableData[currentTabId];

        if (data !== undefined && data !== null) {
          const contentType = typeof data === 'object' ? 'json' : 'markdown';
          setCurrentLeftPanelType(contentType);
          setCurrentLeftPanelContent(contentType === 'json' ? JSON.stringify(data, null, 2) : (data as string));
        } else {
          setCurrentLeftPanelType('markdown');
          setCurrentLeftPanelContent(`No data available for variable {{${currentTabId}}}.`);
        }
      }
    }
  }, [
    currentTabId,
    selectedSavedResponse,
    pastedResponse,
    llmResponse,
    editableVariableData,
    displayPreviewContent,
    resolvedPrompt,
    savedResponses,
    selectedVariableId,
    isDataMode,
    isVarEditMode, // Add this to prevent content updates during edit mode
  ]);

  const containerRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);
  const pointerIdRef = useRef<number | null>(null);
  const moveHandlerRef = useRef<((ev: PointerEvent) => void) | null>(null);
  const upHandlerRef = useRef<((ev: PointerEvent) => void) | null>(null);

  const handleRun = async () => {
    if (onRun) {
      try {
        // Start loading state
        setIsLoading(true);

        // Resolve variables in the prompt using editable data
        const resolvedPromptText = resolvePromptVariables(editorContent, editableVariableData);

        // Store the resolved prompt
        setResolvedPrompt(resolvedPromptText);

        // Track execution time
        const startTime = performance.now();

        // Send to LLM and get response
        const response = await onRun(resolvedPromptText, editableVariableData, selectedModel);

        // Calculate execution time
        const endTime = performance.now();
        const executionTime = Math.round(endTime - startTime);

        // Store the LLM response
        setLlmResponse(response);

        // Auto-save the Run response - keep all Run results for comparison
        const now = new Date();
        const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        // Save to cache (internal run)
        const runTitle = `Run - ${dateStr} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
        AIEditorCacheService.saveResponse(
          reportData,
          resolvedPromptText,
          response,
          'internal',
          selectedModel,
          'KitchenSync',
          runTitle,
          executionTime
        );
        const timeStr = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

        const runResponse: SavedResponse = {
          id: `run-${Date.now()}`,
          title: `R-${selectedModel} - ${dateStr} ${timeStr}`,
          content: response,
          timestamp: now,
          source: 'Run',
          model: selectedModel,
          executionTime: executionTime,
        };
        setSavedResponses(prev => {
          // Add new Run result at the beginning, keeping all previous results
          return [runResponse, ...prev];
        });
      } catch (error) {
        console.error('Error running prompt:', error);
        // Set error message in the preview tab content
        setLlmResponse(`Error: ${error instanceof Error ? error.message : 'Failed to process prompt'}`);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Enhanced pointer handling for both mouse and touch events
  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();

    pointerIdRef.current = e.pointerId;
    setIsResizing(true);

    const handleMove = (ev: PointerEvent) => {
      if (pointerIdRef.current !== ev.pointerId || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const pointerX = ev.clientX - containerRect.left;

      // Calculate percentage (constrain between 20% and 80%)
      let newWidthPercent = (pointerX / containerWidth) * 100;
      newWidthPercent = Math.max(20, Math.min(80, newWidthPercent));

      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        setLeftPanelWidth(newWidthPercent);
      });
    };

    const handleUpOrCancel = (ev: PointerEvent) => {
      if (pointerIdRef.current !== ev.pointerId) return;

      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUpOrCancel);
      window.removeEventListener('pointercancel', handleUpOrCancel);

      pointerIdRef.current = null;
      moveHandlerRef.current = null;
      upHandlerRef.current = null;
      setIsResizing(false);
    };

    // Save refs for cleanup on unmount just in case
    moveHandlerRef.current = handleMove;
    upHandlerRef.current = handleUpOrCancel;

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUpOrCancel);
    window.addEventListener('pointercancel', handleUpOrCancel);
  };

  // Function to handle downloading the editor content
  const handleDownload = () => {
    // Create a blob with the editor content
    const blob = new Blob([editorContent], { type: 'text/plain' });

    // Create a URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element
    const a = document.createElement('a');
    a.href = url;
    a.download = 'prompt.md'; // Default filename with markdown extension

    // Append to the document, click it, and then remove it. This is only to be able to downlad the file.
    // @ts-ignore
    document.body.appendChild(a);
    a.click();
    // @ts-ignore
    document.body.removeChild(a);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  };

  // Cleanup any dangling listeners on unmount
  useEffect(() => {
    return () => {
      if (moveHandlerRef.current) {
        window.removeEventListener('pointermove', moveHandlerRef.current);
        moveHandlerRef.current = null;
      }
      if (upHandlerRef.current) {
        window.removeEventListener('pointerup', upHandlerRef.current);
        window.removeEventListener('pointercancel', upHandlerRef.current);
        upHandlerRef.current = null;
      }
    };
  }, []);

  const handlePromptCopy = () => {
    navigator.clipboard.writeText(editorContent);
  };

  const handleLeftPanelCopy = () => {
    navigator.clipboard.writeText(currentLeftPanelContent as string);
  };

  const handlePasteResponse = () => {
    setShowPasteModal(true);
  };

  const handlePasteSubmit = (content: string, title: string, source?: string) => {
    setPastedResponse(content);
    setShowPasteModal(false);

    // Save to cache (external response)
    AIEditorCacheService.saveResponse(
      reportData,
      editorContent, // Use current editor content as prompt
      content,
      'external',
      undefined, // No model info for pasted content
      source || 'Manual',
      title || `Pasted - ${new Date().toLocaleTimeString()}`,
      undefined // No execution time for pasted content
    );

    // Save the pasted response - insert after Run results but before other responses
    const savedResponse: SavedResponse = {
      id: `paste-${Date.now()}`,
      title: title || `Pasted - ${new Date().toLocaleTimeString()}`,
      content,
      timestamp: new Date(),
      source: source || 'Manual',
    };
    setSavedResponses(prev => {
      const runResponses = prev.filter(r => r.source === 'Run');
      const nonRunResponses = prev.filter(r => r.source !== 'Run');
      // Add new pasted response right after Run responses (from the left)
      return [...runResponses, savedResponse, ...nonRunResponses];
    });

    // Select the new response and switch to preview tab
    setSelectedSavedResponse(savedResponse);
    setSelectedResponseId(savedResponse.id);
    setCurrentTabId('preview');
  };

  const handlePasteCancel = () => {
    setShowPasteModal(false);
  };

  const handleClearPasted = () => {
    setPastedResponse('');
    setSelectedSavedResponse(null);
  };

  const handleResponseSelect = (id: string) => {
    const response = savedResponses.find(r => r.id === id);
    if (response) {
      setSelectedSavedResponse(response);
      setSelectedResponseId(id);
    }
  };

  const handleResponseDelete = (responseId: string) => {
    // Extract timestamp from response ID to delete the correct cached entry
    const timestampMatch = responseId.match(/-(\d+)$/);
    if (timestampMatch) {
      const timestamp = parseInt(timestampMatch[1]);
      AIEditorCacheService.deleteSpecificResponse(reportData, timestamp);
    }

    // Remove from UI state
    setSavedResponses(prev => prev.filter(r => r.id !== responseId));

    // If we deleted the currently selected response, clear it
    if (selectedSavedResponse?.id === responseId) {
      setSelectedSavedResponse(null);
      setSelectedResponseId(null);
    }
  };

  const handlePurgeAllResponses = () => {
    // Clear all responses from localStorage
    AIEditorCacheService.purgeAllResponses(reportData);

    // Clear UI state
    setSavedResponses([]);
    setSelectedSavedResponse(null);
    setSelectedResponseId(null);
  };

  const handleTogglePreviewMode = () => {
    setIsDataMode(!isDataMode);
  };

  const handleOpenSavedResponses = () => {
    if (currentTabId === 'saved-responses') {
      // If already in saved-responses, go back to preview
      setCurrentTabId('preview');
    } else {
      // Otherwise, switch to saved-responses
      setCurrentTabId('saved-responses');
    }
  };

  const handleToggleConstrain = () => {
    const newConstrainedState = !isConstrained;
    setIsConstrained(newConstrainedState);
    if (onConstrainChange) {
      onConstrainChange(newConstrainedState);
    }
  };

  // Version management handlers
  const handleVersionChange = (versionId: string) => {
    if (!versionGroup) return;

    const updatedGroup = PromptVersionService.switchToVersion(reportData, versionId);
    if (updatedGroup) {
      setVersionGroup(updatedGroup);
      const activeVersion = updatedGroup.versions.find(v => v.id === versionId);
      if (activeVersion) {
        setEditorContent(activeVersion.content);
      }
    }
  };

  const handleNewVersion = () => {
    //console.log('Creating new version with content:', editorContent);
    const updatedGroup = PromptVersionService.createNewVersion(reportData, editorContent);
    if (updatedGroup) {
      //console.log('New version created:', updatedGroup);
      setVersionGroup(updatedGroup);
      setHasUnsavedChanges(false);
    } else {
      console.error('Failed to create new version');
    }
  };

  const handleSave = () => {
    // If no version group exists yet, initialize it first
    if (!versionGroup && prompt) {
      const newGroup = PromptVersionService.initializeVersionGroup(reportData, prompt);
      setVersionGroup(newGroup);
      setHasUnsavedChanges(false);
      return;
    }

    if (!versionGroup || !hasUnsavedChanges) {
      //console.log('Save skipped:', { versionGroup: !!versionGroup, hasUnsavedChanges });
      return;
    }

    //console.log('Saving version:', versionGroup.activeVersionId, 'with content:', editorContent);
    const updatedGroup = PromptVersionService.saveVersion(reportData, versionGroup.activeVersionId, editorContent);
    if (updatedGroup) {
      //console.log('Version saved:', updatedGroup);
      setVersionGroup(updatedGroup);
      setHasUnsavedChanges(false);
    } else {
      console.error('Failed to save version');
    }
  };

  const handleDeleteVersion = () => {
    if (!versionGroup || !versionGroup.activeVersionId) return;

    const activeVersion = versionGroup.versions.find(v => v.id === versionGroup.activeVersionId);
    if (!activeVersion || activeVersion.isOriginal) return; // Can't delete original

    const updatedGroup = PromptVersionService.deleteVersion(reportData, versionGroup.activeVersionId);
    if (updatedGroup) {
      setVersionGroup(updatedGroup);
      // Switch to the new active version content (should be original after deletion)
      const newActiveVersion = updatedGroup.versions.find(v => v.id === updatedGroup.activeVersionId);
      if (newActiveVersion) {
        setEditorContent(newActiveVersion.content);
      }
      setHasUnsavedChanges(false);
    } else {
      console.error('Failed to delete version');
    }
  };

  const handleClearCache = () => {
    // Clear prompt versions (global)
    PromptVersionService.clearVersions(reportData);
    setVersionGroup(null);
    setHasUnsavedChanges(false);

    // Show a brief notification
    alert('Prompt versions cleared successfully!');
  };

  return (
    <div className="ai-editor" ref={containerRef}>
      {/* Left Panel */}
      <div className="ai-editor__left-panel" ref={leftPanelRef} style={{ flex: `0 0 ${leftPanelWidth}%` }}>
        <LeftPanelHeader
          tabs={generateTabs()}
          activeTabId={currentTabId === 'vars' ? selectedVariableId || undefined : currentTabId}
          variableIds={extractVariables(editorContent)}
          onTabChange={handleTabChange}
          onCopy={
            // Hide copy button when in Preview mode (component view) or Saved Responses
            (currentTabId === 'preview' && !isDataMode) || currentTabId === 'saved-responses'
              ? undefined
              : handleLeftPanelCopy
          }
          onPaste={handlePasteResponse}
          onClearPasted={currentTabId === 'preview' && pastedResponse ? handleClearPasted : undefined}
          savedResponses={savedResponses}
          selectedResponseId={selectedResponseId}
          onResponseSelect={handleResponseSelect}
          onResponseDelete={handleResponseDelete}
          onTogglePreviewMode={handleTogglePreviewMode}
          onOpenSavedResponses={handleOpenSavedResponses}
          isDataMode={isDataMode}
          onToggleVarEdit={() => {
            // If we're exiting edit mode, update the resolved prompt
            if (isVarEditMode) {
              const newResolvedPrompt = resolvePromptVariables(editorContent, editableVariableData);
              setResolvedPrompt(newResolvedPrompt);
            }
            setIsVarEditMode(!isVarEditMode);
          }}
          isVarEditMode={isVarEditMode}
          onToggleConstrain={handleToggleConstrain}
          isConstrained={isConstrained}
        />
        <div className="ai-editor__left-content">
          {/* Content visualizer with white background for preview tab */}
          <LeftPanelVisualizer
            contentType={currentLeftPanelType}
            data={currentLeftPanelContent}
            isPreview={currentTabId === 'preview'}
            isEditMode={isVarEditMode && Object.keys(editableVariableData).includes(currentTabId)}
            onDataChange={handleVariableDataChange}
          />
        </div>
      </div>

      {/* Resizer */}
      <div
        className={`ai-editor__resizer ${isResizing ? 'active' : ''}`}
        ref={resizerRef}
        onPointerDown={handlePointerDown}
        style={{ touchAction: 'none' }}
      />

      {/* Right Panel */}
      <div className="ai-editor__right-panel" style={{ flex: `0 0 ${100 - leftPanelWidth}%` }}>
        <RightPanelHeader
          onClose={onClose}
          onDownload={onDownload || handleDownload}
          onCopy={handlePromptCopy}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          versions={versionGroup?.versions || []}
          activeVersionId={versionGroup?.activeVersionId}
          onVersionChange={handleVersionChange}
          onNewVersion={handleNewVersion}
          onSave={handleSave}
          onDeleteVersion={handleDeleteVersion}
          hasUnsavedChanges={hasUnsavedChanges}
          onClearCache={handleClearCache}
        />
        <div className="ai-editor__right-content">
          <RightPanelEditor
            value={editorContent}
            onChange={value => {
              setEditorContent(value);
              // You might want to trigger onRun here or have a debounce
            }}
            placeholder="Enter your prompt here..."
          />
        </div>
        <RightPanelFooter onRun={handleRun} />
      </div>

      {/* Paste Modal */}
      {showPasteModal && (
        <div className="ai-editor__paste-modal-overlay">
          <div className="ai-editor__paste-modal">
            <div className="ai-editor__paste-modal-header">
              <h3>Paste LLM Response</h3>
              <button className="ai-editor__paste-modal-close" onClick={handlePasteCancel} type="button">
                ×
              </button>
            </div>
            <div className="ai-editor__paste-modal-content">
              <div className="ai-editor__paste-form">
                <div className="ai-editor__paste-form-row">
                  <div className="ai-editor__paste-form-field">
                    <label htmlFor="paste-title">Title</label>
                    <input
                      id="paste-title"
                      type="text"
                      className="ai-editor__paste-input"
                      placeholder="e.g., ChatGPT - First attempt"
                      defaultValue={`Response - ${new Date().toLocaleTimeString()}`}
                    />
                  </div>
                  <div className="ai-editor__paste-form-field">
                    <label htmlFor="paste-source">Source</label>
                    <select id="paste-source" className="ai-editor__paste-select">
                      <option value="Manual">Manual</option>
                      <option value="ChatGPT">ChatGPT</option>
                      <option value="Claude">Claude</option>
                      <option value="Gemini">Gemini</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="ai-editor__paste-form-field">
                  <label htmlFor="paste-content">Content</label>
                  <textarea
                    id="paste-content"
                    className="ai-editor__paste-textarea"
                    placeholder="Paste your LLM response here..."
                    rows={12}
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        const content = (e.target as HTMLTextAreaElement).value;
                        const title = (document.getElementById('paste-title') as HTMLInputElement)?.value || '';
                        const source =
                          (document.getElementById('paste-source') as HTMLSelectElement)?.value || 'Manual';
                        if (content.trim()) {
                          handlePasteSubmit(content, title, source);
                        }
                      }
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        handlePasteCancel();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="ai-editor__paste-modal-footer">
              <button
                className="ai-editor__paste-modal-button ai-editor__paste-modal-button--cancel"
                onClick={handlePasteCancel}
                type="button"
              >
                Cancel
              </button>
              <button
                className="ai-editor__paste-modal-button ai-editor__paste-modal-button--submit"
                onClick={() => {
                  const textarea = document.getElementById('paste-content') as HTMLTextAreaElement;
                  const titleInput = document.getElementById('paste-title') as HTMLInputElement;
                  const sourceSelect = document.getElementById('paste-source') as HTMLSelectElement;

                  const content = textarea?.value || '';
                  const title = titleInput?.value || '';
                  const source = sourceSelect?.value || 'Manual';

                  if (content.trim()) {
                    handlePasteSubmit(content, title, source);
                  }
                }}
                type="button"
              >
                Paste (Ctrl+Enter)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIEditor;
